import { useEffect, useRef, useState } from 'react';
import { HERO, HEROES } from '@aleaces/engine';
import type { GameMode } from '@aleaces/protocol';
import { Portrait } from '../components/Portrait';
import { useProfile } from '../hooks';
import { SFX } from '../sfx';

/** After matchmaking: the random hero draw. A short shuffle animation cycles
 * through portraits, then lands on the server-drawn hero. One reroll allowed. */
export function HeroDrawScreen({ mode, heroId, rerollsLeft, deadline, players, onReroll, onReady }: {
  mode: GameMode;
  heroId: string;
  rerollsLeft: number;
  deadline: number;
  players: string[];
  onReroll: () => void;
  onReady: () => void;
}) {
  const p = useProfile();
  const [displayHero, setDisplayHero] = useState(heroId);
  const [spinning, setSpinning] = useState(true);
  const [ready, setReady] = useState(false);
  const [seconds, setSeconds] = useState(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
  const spinTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // shuffle animation whenever the drawn hero changes
  useEffect(() => {
    setSpinning(true);
    let ticks = 0;
    const ids = HEROES.map((h) => h.id);
    spinTimer.current = setInterval(() => {
      ticks++;
      if (ticks >= 10) {
        if (spinTimer.current) clearInterval(spinTimer.current);
        setDisplayHero(heroId);
        setSpinning(false);
        SFX.flip();
      } else {
        setDisplayHero(ids[Math.floor(Math.random() * ids.length)]!);
        SFX.deal();
      }
    }, 110);
    return () => {
      if (spinTimer.current) clearInterval(spinTimer.current);
    };
  }, [heroId]);

  useEffect(() => {
    const t = setInterval(() => setSeconds(Math.max(0, Math.ceil((deadline - Date.now()) / 1000))), 250);
    return () => clearInterval(t);
  }, [deadline]);

  const hero = HERO[displayHero] ?? HEROES[0]!;
  const drawn = HERO[heroId] ?? HEROES[0]!;

  return (
    <div className="overlay-screen">
      <div className="big-eyebrow">{mode === 'ranked' ? 'Ranglistenturnier' : 'Freundschaftsrunde'} · Tisch gefunden</div>
      <h1>Dein Zufallsheld</h1>
      <div className="draw-stage">
        <div className={'draw-card' + (spinning ? ' spinning' : '')} key={heroId}>
          <Portrait heroId={hero.id} skinId={p?.skins[hero.id] ?? 'base'} frame={p?.frame ?? 'wood'} size={130} />
          <div className="hname">{hero.name}</div>
          <div className="hrace">{hero.race}</div>
          {!spinning && (
            <div className="hab">
              <b>{drawn.ability.icon} {drawn.ability.name}</b>
              {drawn.ability.desc}
            </div>
          )}
        </div>
        <div className="draw-actions">
          <button className="btn ghost" disabled={spinning || rerollsLeft <= 0 || ready} onClick={onReroll}>
            Neu ziehen{rerollsLeft > 0 ? ` (${rerollsLeft}×)` : ''}
          </button>
          <button
            className="btn gold"
            disabled={spinning || ready}
            onClick={() => {
              setReady(true);
              onReady();
            }}
          >
            {ready ? 'Warte auf die anderen …' : 'Bereit'}
          </button>
        </div>
        <div className="muted" style={{ fontFamily: 'var(--font-ui)', fontSize: 13 }}>
          Das Spiel beginnt in <b style={{ color: 'var(--gold-hi)' }}>{seconds}s</b> automatisch.
        </div>
      </div>
      {players.length > 1 && (
        <div className="vs-row">
          {players.map((name, i) => <span key={i}>{name}</span>)}
        </div>
      )}
    </div>
  );
}
